const S = {
  wrapper: { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Diagram 1: Z-Transform Definition & the z-Plane ─────────────
function DiagramDefinition() {
  const W = 900, H = 360;

  // Left panel: discrete signal stem plot
  const LP = 14, LW = 300;
  const ox = LP + 50, oy = 290, xsc = 38, ysc = 55;
  const tx = n => ox + n * xsc;
  const ty = v => oy - v * ysc;

  // Sample: x[n] = 0.7^n * u[n], shown for n=0..6
  const samples = [0,1,2,3,4,5,6].map(n => ({ n, v: Math.pow(0.7, n) }));

  // Middle panel: z-plane (unit circle, ROC)
  const MP = LP + LW + 18;
  const MW = 260;
  const zcx = MP + MW / 2, zcy = H / 2 - 10, zr = 88;

  // Right panel: formula anatomy
  const RP = MP + MW + 18;
  const RW = W - RP - 14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>The Z-Transform — Discrete Signal, Definition, and the Complex z-Plane</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── Left panel: stem plot ── */}
        <rect x={LP} y={10} width={LW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={LP+LW/2} y={30} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Discrete-Time Signal x[n]</text>
        <text x={LP+LW/2} y={46} textAnchor="middle" fontSize="11" fill="#555" fontFamily="'Courier New',monospace">x[n] = (0.7)ⁿ u[n]</text>

        {/* Axis */}
        <line x1={ox-12} y1={oy} x2={tx(7)} y2={oy} stroke="#1F3864" strokeWidth="1.5"/>
        <polygon points={`${tx(7)},${oy} ${tx(7)-7},${oy-3} ${tx(7)-7},${oy+3}`} fill="#1F3864"/>
        <line x1={ox} y1={oy+8} x2={ox} y2={ty(1.15)} stroke="#1F3864" strokeWidth="1.5"/>
        <polygon points={`${ox},${ty(1.15)} ${ox-3},${ty(1.15)+7} ${ox+3},${ty(1.15)+7}`} fill="#1F3864"/>
        <text x={tx(7)+8} y={oy+4} fontSize="13" fill="#1F3864" fontWeight="700">n</text>
        <text x={ox+5} y={ty(1.15)-4} fontSize="13" fill="#1F3864" fontWeight="700">x</text>

        {/* Grid lines */}
        {[0.5,1.0].map(v => (
          <line key={v} x1={ox-8} y1={ty(v)} x2={tx(6.5)} y2={ty(v)} stroke="#eee" strokeWidth="1"/>
        ))}
        {[0.5,1.0].map(v => (
          <text key={v} x={ox-12} y={ty(v)+4} textAnchor="end" fontSize="10" fill="#bbb">{v}</text>
        ))}

        {/* Stems */}
        {samples.map(({ n, v }) => (
          <g key={n}>
            <line x1={tx(n)} y1={oy} x2={tx(n)} y2={ty(v)} stroke="#2E75B6" strokeWidth="2"/>
            <circle cx={tx(n)} cy={ty(v)} r={5} fill="#2E75B6" stroke="#fff" strokeWidth="1.5"/>
            <text x={tx(n)} y={oy+15} textAnchor="middle" fontSize="11" fill="#888">{n}</text>
          </g>
        ))}

        {/* Annotation arrow for n=0 */}
        <line x1={tx(0)-22} y1={ty(1)-22} x2={tx(0)-4} y2={ty(1)-5} stroke="#c0392b" strokeWidth="1.3"/>
        <text x={tx(0)-28} y={ty(1)-26} fontSize="11" fill="#c0392b" fontWeight="700" textAnchor="middle">x[0]=1</text>

        {/* Decay label */}
        <text x={LP+LW/2} y={H-30} textAnchor="middle" fontSize="11" fill="#2E75B6" fontWeight="700">Causal exponential decay</text>
        <text x={LP+LW/2} y={H-16} textAnchor="middle" fontSize="10" fill="#555">ROC: |z| &gt; 0.7</text>

        {/* ── Middle panel: z-plane ── */}
        <rect x={MP} y={10} width={MW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={MP+MW/2} y={30} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">The z-Plane</text>

        {/* ROC shading: annular region |z|>0.7 */}
        <circle cx={zcx} cy={zcy} r={zr+28} fill="#d0e8ff" fillOpacity="0.35"/>
        <circle cx={zcx} cy={zcy} r={zr*0.80} fill="#fff"/>
        <text x={zcx+zr+10} y={zcy-28} fontSize="10" fill="#2E75B6" fontWeight="700">ROC</text>
        <text x={zcx+zr+10} y={zcy-16} fontSize="10" fill="#2E75B6">|z| &gt; 0.7</text>

        {/* Unit circle */}
        <circle cx={zcx} cy={zcy} r={zr} fill="none" stroke="#2E75B6" strokeWidth="1.8" strokeDasharray="6,3"/>
        <text x={zcx+zr+4} y={zcy+4} fontSize="10" fill="#2E75B6" fontWeight="700">|z|=1</text>

        {/* Axes */}
        <line x1={MP+12} y1={zcy} x2={MP+MW-12} y2={zcy} stroke="#888" strokeWidth="1"/>
        <line x1={zcx} y1={zcy-zr-22} x2={zcx} y2={zcy+zr+22} stroke="#888" strokeWidth="1"/>
        <text x={MP+MW-10} y={zcy+12} fontSize="11" fill="#888">Re</text>
        <text x={zcx+5} y={zcy-zr-18} fontSize="11" fill="#888">Im</text>

        {/* Pole at z=0.7 */}
        <text x={zcx + zr*0.80} y={zcy+2} textAnchor="middle" fontSize="16" fill="#c0392b" fontWeight="700">×</text>
        <text x={zcx + zr*0.80 + 12} y={zcy-8} fontSize="10" fill="#c0392b" fontWeight="700">z=0.7</text>

        {/* Origin */}
        <circle cx={zcx} cy={zcy} r={3} fill="#888"/>
        <text x={zcx+5} y={zcy+12} fontSize="10" fill="#888">0</text>

        {/* Tick marks on Re axis */}
        {[-1,1].map(v => (
          <g key={v}>
            <line x1={zcx+v*zr} y1={zcy-4} x2={zcx+v*zr} y2={zcy+4} stroke="#bbb" strokeWidth="1"/>
            <text x={zcx+v*zr} y={zcy+15} textAnchor="middle" fontSize="10" fill="#bbb">{v}</text>
          </g>
        ))}

        {/* ── Right panel: formula anatomy ── */}
        <rect x={RP} y={10} width={RW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={RP+RW/2} y={30} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Definition</text>

        {/* Big formula */}
        <rect x={RP+8} y={38} width={RW-16} height={44} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={RP+RW/2} y={57} textAnchor="middle" fontSize="13" fill="#1F3864" fontFamily="'Courier New',monospace" fontWeight="700">X(z) = Σ x[n] z⁻ⁿ</text>
        <text x={RP+RW/2} y={74} textAnchor="middle" fontSize="11" fill="#555" fontFamily="'Courier New',monospace">n = −∞ to +∞  (bilateral)</text>

        {/* Parts */}
        {[
          { y:98,  bg:'#f0fff4', bc:'#27ae60', lbl:'x[n]',  desc:'Discrete-time sequence',    note:'Values at integer sample indices n' },
          { y:148, bg:'#fff7ee', bc:'#e07b00', lbl:'z',     desc:'Complex variable z = re^{jω}', note:'r = magnitude, ω = digital frequency' },
          { y:198, bg:'#f5d0ff', bc:'#9b59b6', lbl:'z⁻ⁿ',  desc:'Delay operator',             note:'z⁻¹ shifts sequence right by 1' },
          { y:248, bg:'#fff0f0', bc:'#c0392b', lbl:'X(z)',  desc:'Z-transform (output)',        note:'Algebraic function of complex z' },
          { y:298, bg:'#EBF3FB', bc:'#2E75B6', lbl:'ROC',   desc:'Region of Convergence',       note:'|z| where the series converges absolutely' },
        ].map(({ y, bg, bc, lbl, desc, note }) => (
          <g key={lbl}>
            <rect x={RP+8} y={y} width={RW-16} height={44} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            <text x={RP+18} y={y+16} fontSize="13" fill={bc} fontFamily="'Courier New',monospace" fontWeight="700">{lbl}</text>
            <text x={RP+18+38} y={y+16} fontSize="11" fill="#1F3864" fontWeight="700">— {desc}</text>
            <text x={RP+18} y={y+33} fontSize="10" fill="#444">{note}</text>
          </g>
        ))}

      </svg>
      <div style={S.dCaption}>The Z-transform maps a discrete-time sequence x[n] to a function X(z) of a complex variable z. The region of convergence (ROC) in the z-plane specifies where the sum converges; for a causal decaying signal the ROC is the exterior of a circle centered at the origin.</div>
    </div>
  );
}

// ── Diagram 2: Common Z-Transform Pairs & Properties ────────────
function DiagramPairs() {
  const W = 900, H = 380;
  const PW = (W - 42) / 2;
  const p = i => 14 + i * (PW + 14);

  // Left: visual table of pairs as coloured rows (rendered in SVG)
  const pairs = [
    { sig:'δ[n]',         zt:'1',                roc:'All z',     color:'#2E75B6', bg:'#EBF3FB' },
    { sig:'u[n]',         zt:'z / (z−1)',         roc:'|z|>1',     color:'#27ae60', bg:'#f0fff4' },
    { sig:'aⁿ u[n]',      zt:'z / (z−a)',         roc:'|z|>|a|',   color:'#e07b00', bg:'#fff7ee' },
    { sig:'n·aⁿ u[n]',    zt:'az / (z−a)²',       roc:'|z|>|a|',   color:'#9b59b6', bg:'#f5d0ff' },
    { sig:'cos(ω₀n)u[n]', zt:'z(z−cosω₀)/(z²−2z cosω₀+1)', roc:'|z|>1', color:'#c0392b', bg:'#fff0f0' },
    { sig:'sin(ω₀n)u[n]', zt:'z sinω₀/(z²−2z cosω₀+1)',    roc:'|z|>1', color:'#1F3864', bg:'#e8eaf6' },
  ];

  // Right: properties as coloured rows
  const props = [
    { name:'Linearity',       formula:'Z{ax[n]+by[n]} = aX(z)+bY(z)',   color:'#2E75B6', bg:'#EBF3FB' },
    { name:'Time shift',      formula:'Z{x[n−k]} = z⁻ᵏ X(z)',          color:'#27ae60', bg:'#f0fff4' },
    { name:'Scaling in z',    formula:'Z{aⁿ x[n]} = X(z/a)',            color:'#e07b00', bg:'#fff7ee' },
    { name:'Time reversal',   formula:'Z{x[−n]} = X(1/z)',              color:'#9b59b6', bg:'#f5d0ff' },
    { name:'Convolution',     formula:'Z{x[n]*h[n]} = X(z)·H(z)',       color:'#c0392b', bg:'#fff0f0' },
    { name:'Differentiation', formula:'Z{n·x[n]} = −z dX/dz',          color:'#1F3864', bg:'#e8eaf6' },
    { name:'Initial value',   formula:'x[0] = lim_{z→∞} X(z)',         color:'#2E75B6', bg:'#EBF3FB' },
    { name:'Final value',     formula:'x[∞] = lim_{z→1} (z−1)X(z)',    color:'#27ae60', bg:'#f0fff4' },
  ];

  const rowH = 44, hdrH = 36;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Common Z-Transform Pairs and Key Properties</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── Left: pairs ── */}
        <rect x={p(0)} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={p(0)+PW/2} y={30} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Common Transform Pairs</text>

        {/* Header row */}
        <rect x={p(0)+8} y={hdrH} width={PW-16} height={22} rx={2} fill="#2E75B6"/>
        <text x={p(0)+20}       y={hdrH+15} fontSize="11" fill="#fff" fontWeight="700">x[n]</text>
        <text x={p(0)+130}      y={hdrH+15} fontSize="11" fill="#fff" fontWeight="700">X(z)</text>
        <text x={p(0)+PW-70}    y={hdrH+15} fontSize="11" fill="#fff" fontWeight="700">ROC</text>

        {pairs.map(({ sig, zt, roc, color, bg }, i) => {
          const y = hdrH + 22 + i * rowH;
          return (
            <g key={sig}>
              <rect x={p(0)+8} y={y} width={PW-16} height={rowH-2} rx={2} fill={bg} stroke={color} strokeWidth="0.8"/>
              <text x={p(0)+20}    y={y+14} fontSize="11" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">{sig}</text>
              <text x={p(0)+130}   y={y+14} fontSize="10" fill="#333" fontFamily="'Courier New',monospace">{zt}</text>
              <text x={p(0)+PW-70} y={y+14} fontSize="10" fill="#555" fontFamily="'Courier New',monospace">{roc}</text>
            </g>
          );
        })}

        {/* ── Right: properties ── */}
        <rect x={p(1)} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={p(1)+PW/2} y={30} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Z-Transform Properties</text>

        {/* Header */}
        <rect x={p(1)+8} y={hdrH} width={PW-16} height={22} rx={2} fill="#2E75B6"/>
        <text x={p(1)+20}  y={hdrH+15} fontSize="11" fill="#fff" fontWeight="700">Property</text>
        <text x={p(1)+140} y={hdrH+15} fontSize="11" fill="#fff" fontWeight="700">Formula</text>

        {props.map(({ name, formula, color, bg }, i) => {
          const y = hdrH + 22 + i * rowH;
          return (
            <g key={name}>
              <rect x={p(1)+8} y={y} width={PW-16} height={rowH-2} rx={2} fill={bg} stroke={color} strokeWidth="0.8"/>
              <text x={p(1)+20}  y={y+14} fontSize="11" fill={color} fontWeight="700">{name}</text>
              <text x={p(1)+140} y={y+14} fontSize="10" fill="#333" fontFamily="'Courier New',monospace">{formula}</text>
            </g>
          );
        })}

      </svg>
      <div style={S.dCaption}>The transform pairs table converts common sequences directly. The properties (linearity, time-shift, convolution) are what make the Z-transform powerful for solving difference equations — each operation in the time domain becomes an algebraic operation on X(z).</div>
    </div>
  );
}

// ── Diagram 3: Solving a Difference Equation Step-by-Step ────────
function DiagramSolvingDE() {
  const W = 900, H = 400;

  // Flowchart: 5 steps across the top half, solution in bottom half
  const steps = [
    { label:'1. Write difference equation',  note:'y[n]−0.5y[n−1]=x[n]',                color:'#2E75B6', bg:'#EBF3FB', bc:'#2E75B6' },
    { label:'2. Take Z-transform',           note:'Y−0.5z⁻¹Y = X(z)',                   color:'#27ae60', bg:'#f0fff4', bc:'#27ae60' },
    { label:'3. Solve for Y(z)',             note:'Y(z) = X(z)/(1−0.5z⁻¹)',             color:'#e07b00', bg:'#fff7ee', bc:'#e07b00' },
    { label:'4. Partial fractions',          note:'Decompose Y(z)/z into simple terms',  color:'#9b59b6', bg:'#f5d0ff', bc:'#9b59b6' },
    { label:'5. Inverse Z-transform',        note:'y[n] = (0.5)ⁿ u[n]  (causal)',       color:'#c0392b', bg:'#fff0f0', bc:'#c0392b' },
  ];

  const bx = 14, bw = 860, bh = 52, gap = 8;
  const totalFlowH = steps.length * (bh + gap) + 10;

  // Bottom: time-domain output stem plot
  const ox = 110, oy = totalFlowH + 110, xsc = 76, ysc = 70;
  const tx = n => ox + n * xsc;
  const ty = v => oy - v * ysc;
  const solSamples = [0,1,2,3,4,5,6,7,8,9].map(n => ({ n, v: Math.pow(0.5, n) }));

  // Ensure oy is within H
  const safeOy = Math.min(oy, H - 60);

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Solving a First-Order Difference Equation with the Z-Transform</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Background */}
        <rect x={bx} y={8} width={bw} height={H-16} rx={6} fill="#fff" stroke="#ccd9ea"/>

        {/* ── Flowchart steps ── */}
        {steps.map(({ label, note, bg, bc }, i) => {
          const y = 18 + i * (bh + gap);
          const w = bw - 20;
          return (
            <g key={i}>
              <rect x={bx+10} y={y} width={w} height={bh} rx={4} fill={bg} stroke={bc} strokeWidth="1.4"/>
              <text x={bx+24} y={y+18} fontSize="12" fill="#1F3864" fontWeight="700">{label}</text>
              <text x={bx+24} y={y+36} fontSize="12" fill="#444" fontFamily="'Courier New',monospace">{note}</text>
              {i < steps.length - 1 && (
                <polygon
                  points={`${bx+bw/2},${y+bh+2} ${bx+bw/2-7},${y+bh+gap-1} ${bx+bw/2+7},${y+bh+gap-1}`}
                  fill="#bbb"/>
              )}
            </g>
          );
        })}

        {/* ── Output stem plot ── */}
        {/* Title */}
        <text x={W/2} y={totalFlowH+22} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Output y[n] = (0.5)ⁿ u[n]
        </text>

        {/* Axes */}
        <line x1={ox-14} y1={safeOy} x2={tx(9)+30} y2={safeOy} stroke="#1F3864" strokeWidth="1.5"/>
        <polygon points={`${tx(9)+30},${safeOy} ${tx(9)+22},${safeOy-4} ${tx(9)+22},${safeOy+4}`} fill="#1F3864"/>
        <line x1={ox} y1={safeOy+10} x2={ox} y2={ty(1.1)} stroke="#1F3864" strokeWidth="1.5"/>
        <polygon points={`${ox},${ty(1.1)} ${ox-3},${ty(1.1)+7} ${ox+3},${ty(1.1)+7}`} fill="#1F3864"/>
        <text x={tx(9)+36} y={safeOy+4} fontSize="12" fill="#1F3864" fontWeight="700">n</text>
        <text x={ox+5} y={ty(1.1)-4} fontSize="12" fill="#1F3864" fontWeight="700">y</text>

        {/* Gridline at y=1 */}
        <line x1={ox-8} y1={ty(1)} x2={tx(9)} y2={ty(1)} stroke="#eee" strokeWidth="1"/>
        <text x={ox-12} y={ty(1)+4} textAnchor="end" fontSize="10" fill="#bbb">1</text>
        <line x1={ox-8} y1={ty(0.5)} x2={tx(9)} y2={ty(0.5)} stroke="#eee" strokeWidth="1"/>
        <text x={ox-12} y={ty(0.5)+4} textAnchor="end" fontSize="10" fill="#bbb">0.5</text>

        {/* Stems */}
        {solSamples.map(({ n, v }) => {
          const plotV = Math.min(v, 1.05);
          return (
            <g key={n}>
              <line x1={tx(n)} y1={safeOy} x2={tx(n)} y2={ty(plotV)} stroke="#c0392b" strokeWidth="2.2"/>
              <circle cx={tx(n)} cy={ty(plotV)} r={5} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
              <text x={tx(n)} y={safeOy+15} textAnchor="middle" fontSize="10" fill="#888">{n}</text>
            </g>
          );
        })}

        {/* Decay curve guide */}
        <polyline
          points={solSamples.map(({ n, v }) => `${tx(n)},${ty(Math.min(v,1.05))}`).join(' ')}
          fill="none" stroke="#c0392b" strokeWidth="1" strokeDasharray="4,3" strokeOpacity="0.5"/>

      </svg>
      <div style={S.dCaption}>The Z-transform converts the difference equation into an algebraic equation in Y(z). After solving for Y(z) and performing partial fraction decomposition, the inverse Z-transform (via the pairs table) yields the time-domain solution y[n] directly.</div>
    </div>
  );
}

// ── Diagram 4: Partial Fraction Decomposition & Inverse Z ────────
function DiagramPFD() {
  const W = 900, H = 370;
  const PW = (W - 42) / 2;
  const p = i => 14 + i * (PW + 14);

  // Left: PFD cases
  const cases = [
    {
      title: 'Distinct Real Poles',
      color: '#2E75B6', bg: '#EBF3FB', bc: '#2E75B6',
      formula: 'X(z)/z = A/(z−p₁) + B/(z−p₂)',
      detail: 'A = (z−p₁)X(z)/z |_{z=p₁}',
      result: 'x[n] = A·p₁ⁿ u[n] + B·p₂ⁿ u[n]',
    },
    {
      title: 'Repeated Pole (order 2)',
      color: '#e07b00', bg: '#fff7ee', bc: '#e07b00',
      formula: 'X(z)/z = A/(z−p) + B/(z−p)²',
      detail: 'B = (z−p)²X(z)/z |_{z=p}',
      result: 'Involves n·pⁿ u[n] term',
    },
    {
      title: 'Complex Conjugate Poles',
      color: '#9b59b6', bg: '#f5d0ff', bc: '#9b59b6',
      formula: 'p = re^{jθ},  p* = re^{−jθ}',
      detail: 'Combine to real sinusoidal form',
      result: 'x[n] = C·rⁿ cos(θn+φ) u[n]',
    },
  ];

  const caseH = 86;

  // Right: inverse Z-transform methods
  const methods = [
    { title: 'Pairs Table Lookup',       color: '#2E75B6', bg: '#EBF3FB', bc: '#2E75B6',
      note: 'After PFD, match each term\nto a standard X(z) pair.\nFastest method when poles are simple.' },
    { title: 'Long Division',            color: '#27ae60', bg: '#f0fff4', bc: '#27ae60',
      note: 'Divide numerator by denominator\nas polynomials in z⁻¹.\nReads off x[0], x[1], x[2], … directly.' },
    { title: 'Contour Integration',      color: '#9b59b6', bg: '#f5d0ff', bc: '#9b59b6',
      note: 'x[n] = (1/2πj) ∮ X(z)zⁿ⁻¹ dz\nSum of residues inside ROC.\nExact but requires complex analysis.' },
  ];

  const methH = 86;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Partial Fraction Decomposition and Inverse Z-Transform Methods</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── Left: PFD cases ── */}
        <rect x={p(0)} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={p(0)+PW/2} y={30} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">PFD: Three Pole Cases</text>

        {/* Standard form note */}
        <rect x={p(0)+10} y={36} width={PW-20} height={30} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={p(0)+PW/2} y={50} textAnchor="middle" fontSize="11" fill="#1F3864" fontFamily="'Courier New',monospace" fontWeight="700">Always expand X(z)/z, not X(z)</text>
        <text x={p(0)+PW/2} y={62} textAnchor="middle" fontSize="10" fill="#555">Then multiply back by z at the end</text>

        {cases.map(({ title, color, bg, bc, formula, detail, result }, i) => {
          const y = 74 + i * (caseH + 8);
          return (
            <g key={i}>
              <rect x={p(0)+10} y={y} width={PW-20} height={caseH} rx={4} fill={bg} stroke={bc} strokeWidth="1.2"/>
              <text x={p(0)+20} y={y+17} fontSize="12" fill={color} fontWeight="700">{title}</text>
              <text x={p(0)+20} y={y+34} fontSize="11" fill="#333" fontFamily="'Courier New',monospace">{formula}</text>
              <text x={p(0)+20} y={y+51} fontSize="10" fill="#555" fontFamily="'Courier New',monospace">{detail}</text>
              <text x={p(0)+20} y={y+68} fontSize="10" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">→ {result}</text>
            </g>
          );
        })}

        {/* Tip box */}
        <rect x={p(0)+10} y={74+3*(caseH+8)} width={PW-20} height={32} rx={3} fill="#e8eaf6" stroke="#1F3864" strokeWidth="1"/>
        <text x={p(0)+20} y={74+3*(caseH+8)+14} fontSize="11" fill="#1F3864" fontWeight="700">Stability check:</text>
        <text x={p(0)+20} y={74+3*(caseH+8)+26} fontSize="10" fill="#444">All poles inside unit circle → system is stable</text>

        {/* ── Right: inverse methods ── */}
        <rect x={p(1)} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={p(1)+PW/2} y={30} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Inverse Z-Transform Methods</text>

        {methods.map(({ title, color, bg, bc, note }, i) => {
          const y = 40 + i * (methH + 10);
          return (
            <g key={i}>
              <rect x={p(1)+10} y={y} width={PW-20} height={methH} rx={4} fill={bg} stroke={bc} strokeWidth="1.2"/>
              <text x={p(1)+20} y={y+18} fontSize="13" fill={color} fontWeight="700">{title}</text>
              {note.split('\n').map((line, j) => (
                <text key={j} x={p(1)+20} y={y+34+j*16} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">{line}</text>
              ))}
            </g>
          );
        })}

        {/* Long division worked mini-example */}
        <rect x={p(1)+10} y={40+3*(methH+10)} width={PW-20} height={62} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.2"/>
        <text x={p(1)+20} y={40+3*(methH+10)+16} fontSize="12" fill="#1F3864" fontWeight="700">Long division example:</text>
        <text x={p(1)+20} y={40+3*(methH+10)+32} fontSize="11" fill="#2E75B6" fontFamily="'Courier New',monospace">X(z) = 1/(1−0.5z⁻¹)</text>
        <text x={p(1)+20} y={40+3*(methH+10)+48} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">= 1 + 0.5z⁻¹ + 0.25z⁻² + …</text>
        <text x={p(1)+20} y={40+3*(methH+10)+60} fontSize="10" fill="#555">so x[n] = (0.5)ⁿ for n ≥ 0</text>

      </svg>
      <div style={S.dCaption}>Partial fraction decomposition of X(z)/z (not X(z)) is the standard technique before applying the inverse Z-transform. Each partial fraction term matches a known transform pair. Long division is useful when only the first few values of the sequence are needed.</div>
    </div>
  );
}

// ── Diagram 5: Region of Convergence (ROC) Taxonomy ─────────────
function DiagramROC() {
  const W = 900, H = 360;
  const PW = (W - 56) / 3;
  const p = i => 14 + i * (PW + 14);
  const cy = 165, r = 82;

  const panels = [
    {
      title: 'Causal (Right-sided)',
      subtitle: 'ROC: |z| > r₁',
      color: '#2E75B6', bg: '#EBF3FB', bc: '#2E75B6',
      rocFill: true,   // fill outside circle
      poleAngle: null, // pole at real positive
      poleR: 0.65,
      desc: 'x[n] nonzero for n ≥ N\nROC is exterior of circle\nIncludes z = ∞',
    },
    {
      title: 'Anti-Causal (Left-sided)',
      subtitle: 'ROC: |z| < r₂',
      color: '#c0392b', bg: '#fff0f0', bc: '#c0392b',
      rocFill: false,  // fill inside circle
      poleR: 0.65,
      desc: 'x[n] nonzero for n ≤ N\nROC is interior of circle\nIncludes z = 0',
    },
    {
      title: 'Two-sided (Bilateral)',
      subtitle: 'ROC: r₁ < |z| < r₂',
      color: '#9b59b6', bg: '#f5d0ff', bc: '#9b59b6',
      rocFill: null,   // annular
      poleR1: 0.45,
      poleR2: 0.80,
      desc: 'x[n] nonzero for all n\nROC is an annulus\nMay not include unit circle',
    },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Region of Convergence (ROC) — Three Signal Types in the z-Plane</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {panels.map(({ title, subtitle, color, bg, bc, rocFill, poleR, poleR1, poleR2, desc }, i) => {
          const cx = p(i) + PW / 2;
          return (
            <g key={i}>
              <rect x={p(i)} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
              <text x={cx} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">{title}</text>
              <text x={cx} y={44} textAnchor="middle" fontSize="11" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">{subtitle}</text>

              {/* ROC shading */}
              {rocFill === true && (
                <>
                  {/* exterior: big circle minus inner */}
                  <circle cx={cx} cy={cy} r={r+28} fill={color} fillOpacity="0.12"/>
                  <circle cx={cx} cy={cy} r={r} fill="#fff"/>
                </>
              )}
              {rocFill === false && (
                <circle cx={cx} cy={cy} r={r} fill={color} fillOpacity="0.12"/>
              )}
              {rocFill === null && (
                <>
                  <circle cx={cx} cy={cy} r={r} fill={color} fillOpacity="0.15"/>
                  <circle cx={cx} cy={cy} r={r * poleR1 / 0.45 * 0.45} fill="#fff"/>
                </>
              )}

              {/* Unit circle */}
              <circle cx={cx} cy={cy} r={r} fill="none" stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="5,3"/>
              <text x={cx+r+3} y={cy+4} fontSize="9" fill="#2E75B6">|z|=1</text>

              {/* Axes */}
              <line x1={p(i)+14} y1={cy} x2={p(i)+PW-14} y2={cy} stroke="#aaa" strokeWidth="1"/>
              <line x1={cx} y1={cy-r-20} x2={cx} y2={cy+r+20} stroke="#aaa" strokeWidth="1"/>
              <text x={p(i)+PW-12} y={cy+12} fontSize="9" fill="#aaa">Re</text>
              <text x={cx+4} y={cy-r-16} fontSize="9" fill="#aaa">Im</text>

              {/* Poles */}
              {poleR && (
                <text x={cx + r*poleR} y={cy+3} textAnchor="middle" fontSize="15" fill={color} fontWeight="700">×</text>
              )}
              {poleR1 && (
                <text x={cx + r*poleR1} y={cy+3} textAnchor="middle" fontSize="15" fill={color} fontWeight="700">×</text>
              )}
              {poleR2 && (
                <text x={cx + r*(poleR2/0.8)} y={cy+3} textAnchor="middle" fontSize="15" fill={color} fontWeight="700">×</text>
              )}

              {/* Origin */}
              <circle cx={cx} cy={cy} r={3} fill="#888"/>

              {/* Description box */}
              <rect x={p(i)+10} y={cy+r+14} width={PW-20} height={64} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
              {desc.split('\n').map((line, j) => (
                <text key={j} x={p(i)+18} y={cy+r+30+j*18} fontSize="11" fill="#333">{line}</text>
              ))}
            </g>
          );
        })}

      </svg>
      <div style={S.dCaption}>The ROC determines causality and stability. A causal system's ROC is outside a circle (includes z=∞). An anti-causal system's ROC is inside a circle (includes z=0). A two-sided sequence has an annular ROC. The system is BIBO-stable if and only if the ROC includes the unit circle |z|=1.</div>
    </div>
  );
}

// ── Diagram 6: Transfer Function & System Analysis ───────────────
function DiagramTransferFunction() {
  const W = 900, H = 370;

  // Left: block diagram of an LTI system
  const LP = 14, LW = 390;
  // Right: pole-zero plot + frequency response
  const RP = LP + LW + 18, RW = W - RP - 14;

  // Pole-zero plot
  const pzcx = RP + RW/2, pzcy = 155, pzr = 90;

  // Frequency response (magnitude of H(e^{jω}) for H(z) = z/(z-0.5))
  const omVals = [];
  for (let i = 0; i <= 100; i++) omVals.push(i * Math.PI / 100);
  function Hmag(om) {
    const re = Math.cos(om) - 0.5, im = Math.sin(om);
    return 1 / Math.sqrt(re*re + im*im);
  }
  const maxH = Math.max(...omVals.map(Hmag));
  const frox = RP + 40, froy = 340, frW = RW - 50, frH = 70;
  function frx(om) { return frox + (om / Math.PI) * frW; }
  function fry(v)  { return froy - (v / maxH) * (frH - 4); }
  const frPts = omVals.map(om => `${frx(om)},${fry(Hmag(om))}`).join(' ');

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Transfer Function H(z), Block Diagram, and Frequency Response</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── Left: block diagram ── */}
        <rect x={LP} y={10} width={LW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={LP+LW/2} y={30} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">LTI System Block Diagram</text>

        {/* Signal flow: X(z) → [H(z)] → Y(z) */}
        {/* Input arrow */}
        <line x1={LP+30} y1={100} x2={LP+110} y2={100} stroke="#1F3864" strokeWidth="2"/>
        <polygon points={`${LP+110},100 ${LP+102},96 ${LP+102},104`} fill="#1F3864"/>
        <text x={LP+20} y={96} fontSize="12" fill="#1F3864" fontWeight="700">X(z)</text>

        {/* System box */}
        <rect x={LP+110} y={76} width={120} height={48} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="2"/>
        <text x={LP+170} y={99} textAnchor="middle" fontSize="14" fill="#2E75B6" fontWeight="700" fontFamily="'Courier New',monospace">H(z)</text>
        <text x={LP+170} y={114} textAnchor="middle" fontSize="10" fill="#555">Transfer function</text>

        {/* Output arrow */}
        <line x1={LP+230} y1={100} x2={LP+310} y2={100} stroke="#1F3864" strokeWidth="2"/>
        <polygon points={`${LP+310},100 ${LP+302},96 ${LP+302},104`} fill="#1F3864"/>
        <text x={LP+314} y={104} fontSize="12" fill="#1F3864" fontWeight="700">Y(z)</text>

        {/* Relationship */}
        <rect x={LP+20} y={136} width={LW-40} height={32} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={LP+LW/2} y={150} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700" fontFamily="'Courier New',monospace">Y(z) = H(z) · X(z)</text>
        <text x={LP+LW/2} y={162} textAnchor="middle" fontSize="10" fill="#555">Convolution in time ↔ multiplication in z</text>

        {/* H(z) definition */}
        <rect x={LP+20} y={178} width={LW-40} height={42} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={LP+30} y={195} fontSize="12" fill="#1F3864" fontWeight="700">H(z) = Y(z)/X(z)  (zero initial conditions)</text>
        <text x={LP+30} y={212} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">= (b₀+b₁z⁻¹+…+bₘz⁻ᵐ) / (1+a₁z⁻¹+…+aₙz⁻ⁿ)</text>

        {/* Stability & causality conditions */}
        {[
          { y:230, bg:'#f0fff4', bc:'#27ae60', title:'Causal system:', note:'h[n]=0 for n<0  →  ROC: |z| > max|pole|' },
          { y:272, bg:'#fff0f0', bc:'#c0392b', title:'BIBO Stable:',   note:'All poles strictly inside unit circle |z|<1' },
          { y:314, bg:'#fff7ee', bc:'#e07b00', title:'FIR system:',    note:'Denominator = 1  →  H(z) is a polynomial' },
        ].map(({ y, bg, bc, title, note }) => (
          <g key={title}>
            <rect x={LP+20} y={y} width={LW-40} height={36} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            <text x={LP+30} y={y+14} fontSize="11" fill="#1F3864" fontWeight="700">{title}</text>
            <text x={LP+30} y={y+28} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">{note}</text>
          </g>
        ))}

        {/* ── Right: pole-zero + freq response ── */}
        <rect x={RP} y={10} width={RW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={RP+RW/2} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Pole-Zero Plot &amp; |H(eʲω)|</text>
        <text x={RP+RW/2} y={42} textAnchor="middle" fontSize="11" fill="#555" fontFamily="'Courier New',monospace">H(z) = z/(z−0.5)</text>

        {/* Unit circle */}
        <circle cx={pzcx} cy={pzcy} r={pzr} fill="#d0e8ff" fillOpacity="0.2" stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="5,3"/>
        {/* Axes */}
        <line x1={RP+14} y1={pzcy} x2={RP+RW-14} y2={pzcy} stroke="#aaa" strokeWidth="1"/>
        <line x1={pzcx} y1={pzcy-pzr-18} x2={pzcx} y2={pzcy+pzr+18} stroke="#aaa" strokeWidth="1"/>
        <text x={RP+RW-12} y={pzcy+12} fontSize="9" fill="#aaa">Re</text>
        <text x={pzcx+4} y={pzcy-pzr-14} fontSize="9" fill="#aaa">Im</text>
        {/* ±1 ticks */}
        {[-1,1].map(v => (
          <g key={v}>
            <line x1={pzcx+v*pzr} y1={pzcy-4} x2={pzcx+v*pzr} y2={pzcy+4} stroke="#ccc" strokeWidth="1"/>
            <text x={pzcx+v*pzr} y={pzcy+14} textAnchor="middle" fontSize="9" fill="#bbb">{v}</text>
          </g>
        ))}
        {[-1,1].map(v => (
          <g key={v}>
            <line x1={pzcx-4} y1={pzcy+v*pzr} x2={pzcx+4} y2={pzcy+v*pzr} stroke="#ccc" strokeWidth="1"/>
            <text x={pzcx-8} y={pzcy+v*pzr+4} textAnchor="end" fontSize="9" fill="#bbb">{v}</text>
          </g>
        ))}
        {/* Origin */}
        <circle cx={pzcx} cy={pzcy} r={3} fill="#888"/>
        {/* Zero at z=0 */}
        <circle cx={pzcx} cy={pzcy} r={8} fill="none" stroke="#27ae60" strokeWidth="2"/>
        <text x={pzcx+12} y={pzcy-10} fontSize="10" fill="#27ae60" fontWeight="700">zero z=0</text>
        {/* Pole at z=0.5 */}
        <text x={pzcx + pzr*0.5} y={pzcy+4} textAnchor="middle" fontSize="16" fill="#c0392b" fontWeight="700">×</text>
        <text x={pzcx + pzr*0.5+14} y={pzcy-8} fontSize="10" fill="#c0392b" fontWeight="700">pole z=0.5</text>

        {/* Frequency response plot */}
        <text x={RP+RW/2} y={froy-frH-8} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">|H(eʲω)| vs ω</text>
        <rect x={frox} y={froy-frH} width={frW} height={frH} rx={2} fill="#f8f8ff" stroke="#ddd" strokeWidth="1"/>
        <polyline points={frPts} fill="none" stroke="#2E75B6" strokeWidth="2"/>
        <line x1={frox} y1={froy} x2={frox+frW} y2={froy} stroke="#888" strokeWidth="1"/>
        <text x={frox} y={froy+12} fontSize="9" fill="#888">0</text>
        <text x={frox+frW} y={froy+12} fontSize="9" fill="#888" textAnchor="end">π</text>
        <text x={frox-4} y={froy-frH+4} fontSize="9" fill="#888" textAnchor="end">|H|</text>

      </svg>
      <div style={S.dCaption}>The transfer function H(z) = Y(z)/X(z) completely characterises an LTI system. Poles control decay/growth rates; zeros control cancellation. BIBO stability requires all poles inside the unit circle. The frequency response |H(e^jω)| is H(z) evaluated on the unit circle z = e^jω.</div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────
export default function ZTransforms() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Z-Transforms and Difference Equations</h1>
      <p style={S.p}>The Z-transform is the discrete-time analogue of the Laplace transform. It converts a sequence x[n] into a function X(z) of a complex variable, transforming difference equations into algebraic equations in z. Once solved algebraically, the inverse Z-transform recovers the time-domain sequence. This makes the Z-transform the central tool for analysing discrete-time systems, digital filters, and sampled-data control systems.</p>

      <InfoBox title="Core Idea: Difference equations → algebraic equations via X(z) = Σ x[n] z⁻ⁿ" items={[
        "Z-transform: X(z) = Σ x[n] z⁻ⁿ  (sum over all integers n, or n ≥ 0 for causal signals)",
        "z⁻¹ is the unit-delay operator: multiplying X(z) by z⁻¹ shifts x[n] right by one sample",
        "Region of Convergence (ROC): the set of z values for which the sum converges absolutely",
        "Inverse Z-transform: recover x[n] from X(z) using partial fractions + pairs table, long division, or contour integration",
        "Stability: system is BIBO-stable if and only if all poles of H(z) lie strictly inside the unit circle |z| < 1",
        "Transfer function H(z) = Y(z)/X(z) encodes the complete input-output behaviour of an LTI system",
      ]}/>

      <h2 style={S.h2}>Definition and the z-Plane</h2>
      <DiagramDefinition/>
      <Table2 rows={[
        ["Bilateral Z-transform",  "X(z) = Σ x[n] z⁻ⁿ,  n = −∞ to +∞\nConverges in an annular ROC r₁ < |z| < r₂"],
        ["Unilateral Z-transform", "X(z) = Σ x[n] z⁻ⁿ,  n = 0 to +∞\nUsed for causal signals and initial-condition problems"],
        ["Complex variable z",     "z = r e^{jω} = r(cosω + j sinω)\nr = magnitude, ω = digital angular frequency"],
        ["Unit circle",            "|z| = 1 ↔ z = e^{jω},  ω ∈ [−π, π]\nFrequency response lives on the unit circle"],
        ["Convergence condition",  "Σ |x[n]| |z|⁻ⁿ < ∞\nDetermines the ROC; depends on signal growth rate"],
        ["Link to DTFT",           "X(e^{jω}) = X(z)|_{z=e^{jω}}\nDTFT is the Z-transform evaluated on the unit circle"],
      ]}/>

      <h2 style={S.h2}>Transform Pairs and Properties</h2>
      <DiagramPairs/>
      <Table2 rows={[
        ["Unit impulse δ[n]",     "Z{δ[n]} = 1,  ROC: all z\nIdentity for convolution; tests system impulse response"],
        ["Unit step u[n]",        "Z{u[n]} = z/(z−1),  ROC: |z| > 1\nPole at z=1; marginally stable"],
        ["Geometric aⁿu[n]",     "Z{aⁿu[n]} = z/(z−a),  ROC: |z| > |a|\nDecays if |a|<1, grows if |a|>1"],
        ["Time shift",            "Z{x[n−k]} = z⁻ᵏ X(z)\nFundamental: each z⁻¹ factor ↔ one sample delay"],
        ["Convolution theorem",   "Z{x[n]*h[n]} = X(z)·H(z)\nAllows cascaded systems to multiply transfer functions"],
        ["Scaling (z-domain)",    "Z{aⁿ x[n]} = X(z/a)\nScales the z-axis; shifts poles/zeros radially"],
        ["Initial value theorem", "x[0] = lim_{z→∞} X(z)\nQuick check without inverting the transform"],
        ["Final value theorem",   "x[∞] = lim_{z→1} (z−1)X(z)  (if stable)\nSteady-state value without full inversion"],
      ]}/>

      <h2 style={S.h2}>Solving Difference Equations</h2>
      <DiagramSolvingDE/>
      <Table2 rows={[
        ["General first-order DE",  "y[n] + a₁y[n−1] = b₀x[n]\nZ-transform → (1 + a₁z⁻¹)Y = b₀X"],
        ["General N-th order DE",   "Σ aₖ y[n−k] = Σ bₖ x[n−k]\nY(z) = [Σ bₖ z⁻ᵏ / Σ aₖ z⁻ᵏ] · X(z)"],
        ["With initial conditions", "Unilateral Z-transform includes IC terms\nZ{y[n−1]} = z⁻¹Y(z) + y[−1]"],
        ["Zero-input response",     "X(z)=0; response driven only by initial conditions\nPoles of H(z) appear in this component"],
        ["Zero-state response",     "ICs = 0; response driven only by input\nY_zs(z) = H(z)·X(z)"],
        ["Step response",           "X(z) = z/(z−1); Y(z) = H(z)·z/(z−1)\nInverse Z-transform gives cumulative sum"],
      ]}/>

      <h2 style={S.h2}>Partial Fractions and Inverse Z-Transform</h2>
      <DiagramPFD/>
      <Table2 rows={[
        ["Standard PFD form",       "Always expand X(z)/z (not X(z)) into partial fractions\nThen multiply each term by z to match table pairs"],
        ["Residue formula",         "Aₖ = [(z−pₖ) X(z)/z]_{z=pₖ}\nFor simple pole pₖ; generalised for repeated poles"],
        ["Repeated pole of order m","X(z)/z contains A/(z−p)ᵐ terms\nInverse: involves n^{m−1} aⁿ u[n] combinations"],
        ["Complex poles",           "Keep conjugate pairs together\nCombine to get C·rⁿ cos(θn+φ) u[n] in real form"],
        ["Long division",           "Divide to get X(z) = x[0]+x[1]z⁻¹+x[2]z⁻²+…\nReads off sequence values term-by-term"],
        ["Contour integration",     "x[n] = (1/2πj) ∮ X(z)zⁿ⁻¹ dz\nSum of residues of X(z)zⁿ⁻¹ inside ROC boundary"],
      ]}/>

      <h2 style={S.h2}>Region of Convergence</h2>
      <DiagramROC/>
      <Table2 rows={[
        ["ROC definition",          "Set of z for which Σ|x[n]||z|⁻ⁿ converges\nAlways an annulus (or its limits: disk or complement)"],
        ["Right-sided signal",      "x[n]=0 for n<N: ROC is |z| > rₘₐₓ (exterior disk)\nIncludes z=∞; associated with causal systems"],
        ["Left-sided signal",       "x[n]=0 for n>N: ROC is |z| < rₘᵢₙ (interior disk)\nIncludes z=0; anti-causal"],
        ["Two-sided signal",        "ROC is annulus r₁ < |z| < r₂\nCan be empty if r₁ ≥ r₂"],
        ["Stability and ROC",       "BIBO stable ↔ ROC includes unit circle |z|=1\nEquivalent to: all poles inside unit circle"],
        ["ROC and causality",       "Causal + stable → poles inside unit circle\nAnti-causal + stable → poles outside unit circle"],
      ]}/>

      <h2 style={S.h2}>Transfer Function and System Analysis</h2>
      <DiagramTransferFunction/>
      <Table2 rows={[
        ["Transfer function H(z)",  "H(z) = Y(z)/X(z) = B(z)/A(z)\nb-coefficients: numerator; a-coefficients: denominator"],
        ["Poles",                   "Roots of A(z) = 0\nControl natural (homogeneous) response modes"],
        ["Zeros",                   "Roots of B(z) = 0\nCan cancel poles or shape frequency response"],
        ["FIR vs IIR",             "FIR: A(z)=1 (no feedback), always stable\nIIR: A(z)≠1, recursive, poles possible outside |z|=1"],
        ["Cascade connection",      "H(z) = H₁(z)·H₂(z)\nPoles/zeros of cascade = union of individual poles/zeros"],
        ["Frequency response",      "|H(e^{jω})| = magnitude, ∠H(e^{jω}) = phase\nEvaluate H(z) on unit circle z=e^{jω}, ω∈[0,π]"],
        ["Impulse response",        "h[n] = Z⁻¹{H(z)}\nCompletely characterises the LTI system"],
        ["Difference eq ↔ H(z)",   "aₙ ↔ denominator coefficients\nbₙ ↔ numerator coefficients (after normalising a₀=1)"],
      ]}/>

      <InfoBox title="Key Takeaway — Which Tool to Use and When" items={[
        "Solving a difference equation? → Take Z-transform, solve for Y(z)/X(z), partial fractions, inverse Z",
        "Need x[n] from X(z) quickly? → Long division for first few values, or table lookup after PFD",
        "Checking stability? → Find poles of H(z); all must satisfy |pₖ| < 1",
        "Causal or anti-causal? → ROC exterior of a circle = causal; interior = anti-causal",
        "Two-sided signal? → ROC is an annulus; DTFT exists iff unit circle is inside the annulus",
        "Initial conditions present? → Use unilateral Z-transform; ICs appear as additive z-domain terms",
        "Cascade of two systems? → Multiply their transfer functions H(z) = H₁(z)·H₂(z)",
        "Frequency response? → Substitute z = e^{jω} into H(z) and evaluate magnitude/phase",
      ]}/>
    </div>
  );
}